// Typed API client. All requests go through `apiFetch` which:
//   * always credentials: include (so the session cookie travels)
//   * unwraps the standard error envelope into a typed exception
//   * never throws plain Error — always ApiError with code

import type {
  AccountBalanceItem,
  AccountCreate,
  AccountDetail,
  AccountLinkTillerRequest,
  AccountListItem,
  AccountListQuery,
  AccountMappingHistoryItem,
  AccountPatch,
  AccountSnapshotListItem,
  AccountStatusEventListItem,
  AccountSuppressRequest,
  AccountSummaryResponse,
  AccountTransactionItem,
  AccountTypeRow,
  AccountUnlinkTillerRequest,
  AccountNote,
  AccountMoveRequest,
  AppConfig,
  AppConfigPatch,
  AuditLogListItem,
  AuditLogListQuery,
  BalanceTotalsByCustomerResponse,
  BalanceTotalsResponse,
  ChangePasswordRequest,
  CustomReportColumnsResponse,
  CustomReportPreviewRequest,
  CustomReportPreviewResponse,
  ErrorEnvelope,
  AccountContractRenewalRequest,
  AccountPaymentStatusPatch,
  BankInfoOptionRow,
  CitizenshipOptionRow,
  HardDeleteRequest,
  HardDeleteResponse,
  PaymentStatusOptionRow,
  OobTypeRow,
  OobTypeUpsert,
  TesterCompensationResponse,
  TesterStatusOptionRow,
  FrtCategoryRow,
  FrtCategoryUpsert,
  FrtCustomerCreate,
  FrtCustomerListItem,
  FrtCustomerListQuery,
  FrtCustomerPatch,
  FrtCustomerSuppressRequest,
  LargeTransactionsResponse,
  LoginRequest,
  LoginResponse,
  MeResponse,
  MonthlyTrackingResponse,
  NotificationListItem,
  NotificationListQuery,
  Paginated,
  ProductTypeRow,
  ProductTypeUpsert,
  ReportTemplateCreate,
  ReportTemplateDetail,
  ReportTemplateListItem,
  ReportTemplatePatch,
  AccountTypeUpsert,
  TesterCreate,
  TesterDetail,
  TesterListItem,
  TesterListQuery,
  TesterPatch,
  TesterSuppressRequest,
  UploadChunkRequest,
  UploadChunkResponse,
  UploadCreateResponse,
  UploadDetailResponse,
  UploadFinalizeRequest,
  UploadFinalizeResponse,
  UploadHistoryItem,
  UploadRawResponse,
  UploadResolutionsRequest,
  UploadResolutionsResponse,
  UploadSyncStateResponse,
  UserCreateRequest,
  UserCreateResponse,
  UserListItem,
  UserListQuery,
  UserPatchRequest,
  UserSuspendRequest,
  MappingListQuery,
  MappingRow,
  MissingAppAccount,
  UnmatchedTillerAccount,
  BalanceCreate,
  BalanceUpdate,
  BalanceRow,
  ManualEntryView,
  ManualEntryBulkRequest,
  ManualEntryBulkResult,
  ManualPendingRow,
  StaleTillerRow,
  StaleUpdateRow,
  AssetHistoryEntry,
  AssetListQuery,
  AssetOptionRow,
  DeviceDetail,
  DeviceRow,
  DeviceUpsert,
  PhoneNumberDetail,
  PhoneNumberRow,
  PhoneNumberUpsert,
  PhonePortCreate,
  CustomerPlatformCreate,
  CustomerPlatformPatch,
  CustomerPlatformRow,
  CustomerProfileDetail,
  CustomerProfileListQuery,
  CustomerProfileRow,
  CustomerProfileUpsert,
  OobEntry,
  OobStatus,
  OobStatusEvent,
} from '@frt/shared-types';

export class ApiError extends Error {
  status: number;
  code: string;
  details?: unknown;
  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

// In dev, requests go to "/api" and Vite's proxy forwards them to the
// configured Worker. In a production build (Cloudflare Pages, etc.) we
// can't proxy, so the bundle inlines `VITE_API_BASE` at build time and
// requests go directly to the Worker URL. Both cookie and CORS layers are
// configured for cross-origin credentialed requests.
const BASE = ((import.meta as ImportMeta & { env?: Record<string, string> }).env
  ?.VITE_API_BASE ?? '/api') as string;

export async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body && !headers.has('content-type')) {
    headers.set('content-type', 'application/json');
  }
  const res = await fetch(`${BASE}${path}`, { credentials: 'include', ...init, headers });
  const text = await res.text();
  const body = text ? JSON.parse(text) : null;
  if (!res.ok) {
    const err = (body as ErrorEnvelope | null)?.error;
    throw new ApiError(
      res.status,
      err?.code ?? 'UNKNOWN',
      err?.message ?? `request failed: ${res.status}`,
      err?.details,
    );
  }
  return body as T;
}

/** Fetch + download a binary file via Content-Disposition. Used for the
 *  admin import template downloads (xlsx). */
export async function apiDownload(path: string, fallbackFilename: string): Promise<void> {
  const res = await fetch(`${BASE}${path}`, { credentials: 'include' });
  if (!res.ok) {
    let msg = `download failed: ${res.status}`;
    try {
      const body = (await res.json()) as ErrorEnvelope | null;
      if (body?.error?.message) msg = body.error.message;
    } catch {
      /* binary or empty body — keep generic message */
    }
    throw new ApiError(res.status, 'DOWNLOAD_FAILED', msg);
  }
  const blob = await res.blob();
  const cd = res.headers.get('content-disposition') ?? '';
  const m = cd.match(/filename="?([^";]+)"?/i);
  const name = m?.[1] ?? fallbackFilename;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// ---- typed wrappers ----

export const auth = {
  login: (body: LoginRequest) =>
    apiFetch<LoginResponse>('/auth/login', { method: 'POST', body: JSON.stringify(body) }),
  logout: () => apiFetch<{ ok: true }>('/auth/logout', { method: 'POST' }),
  me: () => apiFetch<MeResponse>('/auth/me'),
  changePassword: (body: ChangePasswordRequest) =>
    apiFetch<{ ok: true }>('/auth/change-password', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
};

function buildQuery(params: Record<string, unknown>): string {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    usp.set(k, String(v));
  }
  const q = usp.toString();
  return q ? `?${q}` : '';
}

// ---- Track 1: FRT Customers ----

export const customers = {
  list: (query: FrtCustomerListQuery = {}) =>
    apiFetch<Paginated<FrtCustomerListItem>>(`/frt-customers${buildQuery(query as Record<string, unknown>)}`),
  get: (id: number) => apiFetch<FrtCustomerListItem>(`/frt-customers/${id}`),
  create: (body: FrtCustomerCreate) =>
    apiFetch<FrtCustomerListItem>('/frt-customers', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  update: (id: number, body: FrtCustomerPatch) =>
    apiFetch<FrtCustomerListItem & { cascade_renames?: number }>(`/frt-customers/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  suppress: (id: number, body: FrtCustomerSuppressRequest = {}) =>
    apiFetch<FrtCustomerListItem>(`/frt-customers/${id}/suppress`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  restore: (id: number) =>
    apiFetch<FrtCustomerListItem>(`/frt-customers/${id}/restore`, { method: 'POST' }),
  remove: (id: number, body: HardDeleteRequest) =>
    apiFetch<HardDeleteResponse>(`/frt-customers/${id}`, {
      method: 'DELETE',
      body: JSON.stringify(body),
    }),
};

// ---- Track 1: Testers ----

export const testers = {
  list: (query: TesterListQuery = {}) =>
    apiFetch<Paginated<TesterListItem>>(`/testers${buildQuery(query as Record<string, unknown>)}`),
  get: (id: number) => apiFetch<TesterDetail>(`/testers/${id}`),
  create: (body: TesterCreate) =>
    apiFetch<TesterListItem>('/testers', { method: 'POST', body: JSON.stringify(body) }),
  update: (id: number, body: TesterPatch) =>
    apiFetch<TesterListItem & { cascade_renames?: number }>(`/testers/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  suppress: (id: number, body: TesterSuppressRequest = {}) =>
    apiFetch<TesterListItem>(`/testers/${id}/suppress`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  restore: (id: number) =>
    apiFetch<TesterListItem>(`/testers/${id}/restore`, { method: 'POST' }),
  remove: (id: number, body: HardDeleteRequest) =>
    apiFetch<HardDeleteResponse>(`/testers/${id}`, {
      method: 'DELETE',
      body: JSON.stringify(body),
    }),
};

// ---- Customer platforms + profiles (mig 0017) ----

export const customerPlatforms = {
  forCustomer: (customerId: number) =>
    apiFetch<{ data: CustomerPlatformRow[] }>(`/frt-customers/${customerId}/platforms`),
  list: (query: { frt_customer_id?: number; active?: 'true' | 'false' | 'all' } = {}) =>
    apiFetch<{ data: CustomerPlatformRow[] }>(
      `/customer-platforms${buildQuery(query as Record<string, unknown>)}`,
    ),
  create: (customerId: number, body: CustomerPlatformCreate) =>
    apiFetch<CustomerPlatformRow>(`/frt-customers/${customerId}/platforms`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  update: (id: number, body: CustomerPlatformPatch) =>
    apiFetch<CustomerPlatformRow>(`/customer-platforms/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
};

export const customerProfiles = {
  list: (query: CustomerProfileListQuery = {}) =>
    apiFetch<{ data: CustomerProfileRow[] }>(
      `/customer-profiles${buildQuery(query as Record<string, unknown>)}`,
    ),
  get: (id: number) => apiFetch<CustomerProfileDetail>(`/customer-profiles/${id}`),
  history: (id: number) =>
    apiFetch<{ data: AssetHistoryEntry[] }>(`/customer-profiles/${id}/history`),
  // mig 0019 — OTP channel On/Off + its log
  setOobStatus: (id: number, oobId: number, status: OobStatus, note: string | null) =>
    apiFetch<OobEntry>(`/customer-profiles/${id}/oob/${oobId}/status`, {
      method: 'POST',
      body: JSON.stringify({ status, note }),
    }),
  oobStatusLog: (id: number) =>
    apiFetch<{ data: OobStatusEvent[] }>(`/customer-profiles/${id}/oob-status-log`),
  create: (body: CustomerProfileUpsert) =>
    apiFetch<CustomerProfileDetail>('/customer-profiles', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  update: (id: number, body: Partial<CustomerProfileUpsert>) =>
    apiFetch<CustomerProfileDetail>(`/customer-profiles/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
};

// ---- Devices + phone numbers (mig 0015) ----

export const devices = {
  list: (query: AssetListQuery = {}) =>
    apiFetch<{ data: DeviceRow[] }>(`/devices${buildQuery(query as Record<string, unknown>)}`),
  get: (id: number) => apiFetch<DeviceDetail>(`/devices/${id}`),
  history: (id: number) => apiFetch<{ data: AssetHistoryEntry[] }>(`/devices/${id}/history`),
  create: (body: DeviceUpsert) =>
    apiFetch<DeviceDetail>('/devices', { method: 'POST', body: JSON.stringify(body) }),
  update: (id: number, body: Partial<DeviceUpsert>) =>
    apiFetch<DeviceDetail>(`/devices/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
};

export const phoneNumbers = {
  list: (query: AssetListQuery = {}) =>
    apiFetch<{ data: PhoneNumberRow[] }>(
      `/phone-numbers${buildQuery(query as Record<string, unknown>)}`,
    ),
  get: (id: number) => apiFetch<PhoneNumberDetail>(`/phone-numbers/${id}`),
  history: (id: number) =>
    apiFetch<{ data: AssetHistoryEntry[] }>(`/phone-numbers/${id}/history`),
  create: (body: PhoneNumberUpsert) =>
    apiFetch<PhoneNumberDetail>('/phone-numbers', { method: 'POST', body: JSON.stringify(body) }),
  update: (id: number, body: Partial<PhoneNumberUpsert>) =>
    apiFetch<PhoneNumberDetail>(`/phone-numbers/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  // mig 0020 — porting history (the latest port sets the carrier)
  addPort: (id: number, body: PhonePortCreate) =>
    apiFetch<PhoneNumberDetail>(`/phone-numbers/${id}/ports`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  assignDevice: (id: number, deviceId: number | null) =>
    apiFetch<PhoneNumberDetail>(`/phone-numbers/${id}/device`, {
      method: 'PUT',
      body: JSON.stringify({ device_id: deviceId }),
    }),
};

export const testerCompensation = {
  list: () =>
    apiFetch<TesterCompensationResponse>('/tester-compensation'),
};

// ---- Track 1: Accounts ----

export const accounts = {
  list: (query: AccountListQuery = {}) =>
    apiFetch<Paginated<AccountListItem>>(`/accounts${buildQuery(query as Record<string, unknown>)}`),
  get: (id: number) => apiFetch<AccountDetail>(`/accounts/${id}`),
  // mig 0015 — linked accounts
  links: (id: number) =>
    apiFetch<import('@frt/shared-types').AccountLinksResponse>(`/accounts/${id}/links`),
  link: (id: number, body: import('@frt/shared-types').AccountLinkCreate) =>
    apiFetch<import('@frt/shared-types').AccountLink>(`/accounts/${id}/links`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  unlink: (id: number, linkId: number, body: import('@frt/shared-types').AccountLinkUnlink) =>
    apiFetch<import('@frt/shared-types').AccountLink>(`/accounts/${id}/links/${linkId}/unlink`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  deleteLink: (id: number, linkId: number) =>
    apiFetch<{ ok: true }>(`/accounts/${id}/links/${linkId}`, { method: 'DELETE' }),
  create: (body: AccountCreate) =>
    apiFetch<AccountListItem>('/accounts', { method: 'POST', body: JSON.stringify(body) }),
  update: (id: number, body: AccountPatch) =>
    apiFetch<AccountListItem>(`/accounts/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  suppress: (id: number, body: AccountSuppressRequest = {}) =>
    apiFetch<AccountListItem>(`/accounts/${id}/suppress`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  restore: (id: number) =>
    apiFetch<AccountListItem>(`/accounts/${id}/restore`, { method: 'POST' }),
  moveToManual: (id: number, body: AccountMoveRequest = {}) =>
    apiFetch<AccountListItem>(`/accounts/${id}/move-to-manual`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  moveToTiller: (id: number, body: AccountMoveRequest = {}) =>
    apiFetch<AccountListItem>(`/accounts/${id}/move-to-tiller`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  linkTiller: (id: number, body: AccountLinkTillerRequest) =>
    apiFetch<AccountListItem>(`/accounts/${id}/link-tiller`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  unlinkTiller: (id: number, body: AccountUnlinkTillerRequest = {}) =>
    apiFetch<AccountListItem>(`/accounts/${id}/unlink-tiller`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  balances: (id: number, query: { page?: number; per_page?: number } = {}) =>
    apiFetch<Paginated<AccountBalanceItem>>(
      `/accounts/${id}/balances${buildQuery(query as Record<string, unknown>)}`,
    ),
  transactions: (
    id: number,
    query: { from?: string; to?: string; page?: number; per_page?: number } = {},
  ) =>
    apiFetch<Paginated<AccountTransactionItem>>(
      `/accounts/${id}/transactions${buildQuery(query as Record<string, unknown>)}`,
    ),
  snapshots: (id: number) =>
    apiFetch<{ data: AccountSnapshotListItem[] }>(`/accounts/${id}/snapshots`),
  mappingHistory: (id: number) =>
    apiFetch<{ data: AccountMappingHistoryItem[] }>(`/accounts/${id}/mapping-history`),
  statusHistory: (id: number) =>
    apiFetch<{ data: AccountStatusEventListItem[] }>(`/accounts/${id}/status-history`),
  // mig 0016 — append-only note log, newest first
  notes: (id: number) => apiFetch<{ data: AccountNote[] }>(`/accounts/${id}/notes`),
  addNote: (id: number, body: string) =>
    apiFetch<AccountNote>(`/accounts/${id}/notes`, {
      method: 'POST',
      body: JSON.stringify({ body }),
    }),
  // mig 0010 — Tester Compensation flows
  updatePaymentStatus: (id: number, body: AccountPaymentStatusPatch) =>
    apiFetch<AccountListItem>(`/accounts/${id}/payment-status`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  contractRenewal: (id: number, body: AccountContractRenewalRequest) =>
    apiFetch<AccountListItem>(`/accounts/${id}/contract-renewal`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  remove: (id: number, body: HardDeleteRequest) =>
    apiFetch<HardDeleteResponse>(`/accounts/${id}`, {
      method: 'DELETE',
      body: JSON.stringify(body),
    }),
};

// ---- Track 2: Uploads + Mapping ----

export const uploads = {
  /** POST /uploads with multipart/form-data; FormData is built by the caller. */
  create: async (file: Blob, filename: string): Promise<UploadCreateResponse> => {
    const fd = new FormData();
    fd.append('file', file, filename);
    // Note: we don't set content-type; the browser sets it with the boundary.
    const res = await fetch(`${BASE}/uploads`, {
      method: 'POST',
      credentials: 'include',
      body: fd,
    });
    const text = await res.text();
    const body = text ? JSON.parse(text) : null;
    if (!res.ok) {
      const err = (body as ErrorEnvelope | null)?.error;
      throw new ApiError(
        res.status,
        err?.code ?? 'UNKNOWN',
        err?.message ?? `upload failed: ${res.status}`,
        err?.details,
      );
    }
    return body as UploadCreateResponse;
  },
  list: (query: { page?: number; per_page?: number } = {}) =>
    apiFetch<Paginated<UploadHistoryItem>>(`/uploads${buildQuery(query as Record<string, unknown>)}`),
  get: (id: number) => apiFetch<UploadDetailResponse>(`/uploads/${id}`),
  missingAccounts: (id: number) =>
    apiFetch<{ data: MissingAppAccount[] }>(`/uploads/${id}/missing-accounts`),
  unmatchedTillerAccounts: (id: number) =>
    apiFetch<{ data: UnmatchedTillerAccount[] }>(`/uploads/${id}/unmatched-tiller-accounts`),
  resolutions: (id: number, body: UploadResolutionsRequest) =>
    apiFetch<UploadResolutionsResponse>(`/uploads/${id}/resolutions`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // ---- chunked upload flow ----
  raw: async (file: Blob, filename: string): Promise<UploadRawResponse> => {
    const fd = new FormData();
    fd.append('file', file, filename);
    const res = await fetch(`${BASE}/uploads/raw`, {
      method: 'POST',
      credentials: 'include',
      body: fd,
    });
    const text = await res.text();
    const body = text ? JSON.parse(text) : null;
    if (!res.ok) {
      const err = (body as ErrorEnvelope | null)?.error;
      throw new ApiError(
        res.status,
        err?.code ?? 'UNKNOWN',
        err?.message ?? `upload/raw failed: ${res.status}`,
        err?.details,
      );
    }
    return body as UploadRawResponse;
  },
  syncState: () => apiFetch<UploadSyncStateResponse>('/uploads/sync-state'),
  chunk: (id: number, body: UploadChunkRequest) =>
    apiFetch<UploadChunkResponse>(`/uploads/${id}/chunk`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  finalize: (id: number, body: UploadFinalizeRequest) =>
    apiFetch<UploadFinalizeResponse>(`/uploads/${id}/finalize`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
};

export const mapping = {
  list: (query: MappingListQuery = {}) =>
    apiFetch<Paginated<MappingRow>>(`/mapping${buildQuery(query as Record<string, unknown>)}`),
};

export const lookups = {
  productTypes: {
    list: () => apiFetch<{ data: ProductTypeRow[] }>('/lookups/product-types'),
    create: (body: ProductTypeUpsert) =>
      apiFetch<ProductTypeRow>('/lookups/product-types', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    update: (id: number, body: Partial<ProductTypeUpsert>) =>
      apiFetch<ProductTypeRow>(`/lookups/product-types/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
  },
  accountTypes: {
    list: () => apiFetch<{ data: AccountTypeRow[] }>('/lookups/account-types'),
    create: (body: AccountTypeUpsert) =>
      apiFetch<AccountTypeRow>('/lookups/account-types', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    update: (id: number, body: Partial<AccountTypeUpsert>) =>
      apiFetch<AccountTypeRow>(`/lookups/account-types/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
  },
  frtCategories: {
    list: () => apiFetch<{ data: FrtCategoryRow[] }>('/lookups/frt-categories'),
    create: (body: FrtCategoryUpsert) =>
      apiFetch<FrtCategoryRow>('/lookups/frt-categories', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    update: (id: number, body: Partial<FrtCategoryUpsert>) =>
      apiFetch<FrtCategoryRow>(`/lookups/frt-categories/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
  },
  // mig 0010 — sorted-named lookups
  bankInfoOptions: makeSortedNamedClient<BankInfoOptionRow>('bank-info-options'),
  citizenshipOptions: makeSortedNamedClient<CitizenshipOptionRow>('citizenship-options'),
  testerStatusOptions: makeSortedNamedClient<TesterStatusOptionRow>('tester-status-options'),
  paymentStatusOptions: makeSortedNamedClient<PaymentStatusOptionRow>('payment-status-options'),
  // mig 0017 — platforms a customer can offer
  platformOptions: makeSortedNamedClient<AssetOptionRow>('platform-options'),
  // mig 0020 — phone + device attribute lists
  phoneTestStatusOptions: makeSortedNamedClient<AssetOptionRow>('phone-test-status-options'),
  phonePaymentResponsibilityOptions: makeSortedNamedClient<AssetOptionRow>(
    'phone-payment-responsibility-options',
  ),
  phoneUsageStatusOptions: makeSortedNamedClient<AssetOptionRow>('phone-usage-status-options'),
  phoneCarrierTypeOptions: makeSortedNamedClient<AssetOptionRow>('phone-carrier-type-options'),
  deviceManufacturerOptions: makeSortedNamedClient<AssetOptionRow>('device-manufacturer-options'),
  deviceModelOptions: makeSortedNamedClient<AssetOptionRow>('device-model-options'),
  deviceOsOptions: makeSortedNamedClient<AssetOptionRow>('device-os-options'),
  deviceSimRestrictionOptions: makeSortedNamedClient<AssetOptionRow>('device-sim-restriction-options'),
  deviceRootStatusOptions: makeSortedNamedClient<AssetOptionRow>('device-root-status-options'),
  // mig 0015 — device / phone asset option lists
  deviceOwnershipOptions: makeSortedNamedClient<AssetOptionRow>('device-ownership-options'),
  phoneProviderOptions: makeSortedNamedClient<AssetOptionRow>('phone-provider-options'),
  phonePaymentPlanOptions: makeSortedNamedClient<AssetOptionRow>('phone-payment-plan-options'),
  // mig 0013 — OOB (OTP channel) types
  oobTypes: {
    list: () => apiFetch<{ data: OobTypeRow[] }>('/lookups/oob-types'),
    create: (body: OobTypeUpsert) =>
      apiFetch<OobTypeRow>('/lookups/oob-types', { method: 'POST', body: JSON.stringify(body) }),
    update: (id: number, body: Partial<OobTypeUpsert>) =>
      apiFetch<OobTypeRow>(`/lookups/oob-types/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
  },
};

interface SortedNamedRow {
  id: number;
  name: string;
  active: boolean;
  sort_order: number;
}
interface SortedNamedUpsert {
  name: string;
  active?: boolean;
  sort_order?: number;
}
function makeSortedNamedClient<T extends SortedNamedRow>(slug: string): {
  list: () => Promise<{ data: T[] }>;
  create: (body: SortedNamedUpsert) => Promise<T>;
  update: (id: number, body: Partial<SortedNamedUpsert>) => Promise<T>;
} {
  return {
    list: () => apiFetch<{ data: T[] }>(`/lookups/${slug}`),
    create: (body) =>
      apiFetch<T>(`/lookups/${slug}`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    update: (id, body) =>
      apiFetch<T>(`/lookups/${slug}/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
  };
}

// ---- Track 5: Reports & Dashboard ----

/** Trigger a binary download (xlsx/csv) by hitting an export endpoint and
 * pushing the blob through an anchor click. */
async function downloadFile(path: string, init: RequestInit = {}): Promise<void> {
  const res = await fetch(`${BASE}${path}`, { credentials: 'include', ...init });
  if (!res.ok) {
    const text = await res.text();
    let msg = `download failed: ${res.status}`;
    try {
      const j = JSON.parse(text) as ErrorEnvelope;
      msg = j.error?.message ?? msg;
    } catch {
      /* not JSON */
    }
    throw new ApiError(res.status, 'DOWNLOAD_FAILED', msg);
  }
  const cd = res.headers.get('content-disposition') ?? '';
  const match = /filename="?([^"]+)"?/.exec(cd);
  const filename = match?.[1] ?? 'download';
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export const reports = {
  accountSummary: (asOf: string = 'current') =>
    apiFetch<AccountSummaryResponse>(`/reports/account-summary${buildQuery({ as_of: asOf })}`),
  accountSummaryExport: (asOf: string, format: 'xlsx' | 'csv') =>
    downloadFile(`/reports/account-summary/export${buildQuery({ as_of: asOf, format })}`),
  monthlyTracking: (from: string, to: string) =>
    apiFetch<MonthlyTrackingResponse>(`/reports/monthly-tracking${buildQuery({ from, to })}`),
  monthlyTrackingExport: (from: string, to: string, format: 'xlsx' | 'csv') =>
    downloadFile(`/reports/monthly-tracking/export${buildQuery({ from, to, format })}`),

  customColumns: () => apiFetch<CustomReportColumnsResponse>('/reports/custom/columns'),
  customPreview: (body: CustomReportPreviewRequest) =>
    apiFetch<CustomReportPreviewResponse>('/reports/custom/preview', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  customExport: (body: CustomReportPreviewRequest) =>
    downloadFile('/reports/custom/export', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    }),

  // Long-format exports for Excel pivot use.
  accountsHistory: (
    query: {
      from: string;
      to: string;
      frt_customer_id?: number;
      tester_id?: number;
      frt_category_id?: number;
      account_status?: string;
    },
  ) =>
    apiFetch<{
      from: string;
      to: string;
      rows: import('@frt/shared-types').AccountsHistoryRow[];
    }>(`/reports/accounts-history${buildQuery(query as Record<string, unknown>)}`),
  accountsHistoryExport: (
    query: {
      from: string;
      to: string;
      frt_customer_id?: number;
      tester_id?: number;
      frt_category_id?: number;
      account_status?: string;
    },
    format: 'xlsx' | 'csv',
  ) =>
    downloadFile(
      `/reports/accounts-history/export${buildQuery({ ...query, format } as Record<string, unknown>)}`,
    ),
  transactionsExport: (
    query: {
      from: string;
      to: string;
      frt_customer_id?: number;
      tester_id?: number;
      account_id?: number;
    },
  ) =>
    apiFetch<{
      from: string;
      to: string;
      rows: import('@frt/shared-types').TransactionsExportRow[];
    }>(`/reports/transactions-export${buildQuery(query as Record<string, unknown>)}`),
  transactionsExportDownload: (
    query: {
      from: string;
      to: string;
      frt_customer_id?: number;
      tester_id?: number;
      account_id?: number;
    },
    format: 'xlsx' | 'csv',
  ) =>
    downloadFile(
      `/reports/transactions-export/export${buildQuery({ ...query, format } as Record<string, unknown>)}`,
    ),

  linkedAccounts: (query: { status?: 'linked' | 'previously_linked'; frt_customer_id?: number }) =>
    apiFetch<{ rows: import('@frt/shared-types').LinkedAccountsExportRow[] }>(
      `/reports/linked-accounts${buildQuery(query as Record<string, unknown>)}`,
    ),
  linkedAccountsDownload: (
    query: { status?: 'linked' | 'previously_linked'; frt_customer_id?: number },
    format: 'xlsx' | 'csv',
  ) =>
    downloadFile(
      `/reports/linked-accounts/export${buildQuery({ ...query, format } as Record<string, unknown>)}`,
    ),

  templates: {
    list: () => apiFetch<{ data: ReportTemplateListItem[] }>('/reports/templates'),
    get: (id: number) => apiFetch<ReportTemplateDetail>(`/reports/templates/${id}`),
    create: (body: ReportTemplateCreate) =>
      apiFetch<ReportTemplateDetail>('/reports/templates', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    update: (id: number, body: ReportTemplatePatch) =>
      apiFetch<ReportTemplateDetail>(`/reports/templates/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    delete: (id: number) =>
      apiFetch<{ ok: true }>(`/reports/templates/${id}`, { method: 'DELETE' }),
    runExport: (id: number, body: { as_of_period?: string | null } = {}) =>
      downloadFile(`/reports/templates/${id}/export`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      }),
  },
};

export const dashboard = {
  balanceTotals: () => apiFetch<BalanceTotalsResponse>('/reports/balance-totals'),
  balanceTotalsByCustomer: () =>
    apiFetch<BalanceTotalsByCustomerResponse>('/reports/balance-totals/by-customer'),
  largeTransactions: (query: { since?: string; limit?: number } = {}) =>
    apiFetch<LargeTransactionsResponse>(
      `/transactions/large${buildQuery(query as Record<string, unknown>)}`,
    ),
};

// ---- Track 4: Snapshots ----

export const snapshots = {
  forPeriod: (period: string) =>
    apiFetch<import('@frt/shared-types').SnapshotsForPeriodResponse>(
      `/snapshots/${encodeURIComponent(period)}`,
    ),
  preview: (period: string, query: { cutoff_at?: string } = {}) =>
    apiFetch<import('@frt/shared-types').SnapshotPreviewResponse>(
      `/snapshots/${encodeURIComponent(period)}/preview${buildQuery(query as Record<string, unknown>)}`,
    ),
  freeze: (period: string, body: import('@frt/shared-types').SnapshotFreezeRequest = {}) =>
    apiFetch<import('@frt/shared-types').SnapshotFreezeResponse>(
      `/snapshots/${encodeURIComponent(period)}/freeze`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  detail: (id: number) =>
    apiFetch<import('@frt/shared-types').MonthlySnapshotDetail>(`/snapshots/${id}`),
  amend: (id: number, body: import('@frt/shared-types').SnapshotAmendRequest) =>
    apiFetch<import('@frt/shared-types').SnapshotAmendResponse>(`/snapshots/${id}/amend`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  amendments: (id: number) =>
    apiFetch<import('@frt/shared-types').SnapshotAmendmentsResponse>(
      `/snapshots/${id}/amendments`,
    ),
};

// ---- Track 6: Users / Audit / Config / Notifications ----

export const users = {
  list: (query: UserListQuery = {}) =>
    apiFetch<Paginated<UserListItem>>(`/users${buildQuery(query as Record<string, unknown>)}`),
  create: (body: UserCreateRequest) =>
    apiFetch<UserCreateResponse>('/users', { method: 'POST', body: JSON.stringify(body) }),
  update: (id: number, body: UserPatchRequest) =>
    apiFetch<UserListItem>(`/users/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  suspend: (id: number, body: UserSuspendRequest = {}) =>
    apiFetch<UserListItem>(`/users/${id}/suspend`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
};

// ---- Track 3: balances + manual entry ----

export const balances = {
  create: (body: BalanceCreate) =>
    apiFetch<BalanceRow>('/balances', { method: 'POST', body: JSON.stringify(body) }),
  update: (id: number, body: BalanceUpdate) =>
    apiFetch<BalanceRow>(`/balances/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  remove: (id: number) =>
    apiFetch<{ ok: true }>(`/balances/${id}`, { method: 'DELETE' }),
};

export const manualEntry = {
  view: (period: string) => apiFetch<ManualEntryView>(`/manual-entry/${period}`),
  bulk: (period: string, body: ManualEntryBulkRequest) =>
    apiFetch<ManualEntryBulkResult>(`/manual-entry/${period}/bulk`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
};

export const auditLog = {
  list: (query: AuditLogListQuery = {}) =>
    apiFetch<Paginated<AuditLogListItem>>(
      `/audit-log${buildQuery(query as Record<string, unknown>)}`,
    ),
};

export const config = {
  get: () => apiFetch<AppConfig>('/config'),
  update: (body: AppConfigPatch) =>
    apiFetch<AppConfig>('/config', { method: 'PATCH', body: JSON.stringify(body) }),
};

export interface NotificationsListResponse extends Paginated<NotificationListItem> {
  unread_count: number;
}

export const notifications = {
  list: (query: NotificationListQuery = {}) =>
    apiFetch<NotificationsListResponse>(
      `/notifications${buildQuery(query as Record<string, unknown>)}`,
    ),
  markRead: (id: number) =>
    apiFetch<NotificationListItem>(`/notifications/${id}/read`, { method: 'POST' }),
  markAllRead: () =>
    apiFetch<{ ok: true; marked: number }>('/notifications/read-all', { method: 'POST' }),
};

export interface ImportError {
  row: number;
  message: string;
}
export interface AccountImportResult {
  created: number;
  errors: ImportError[];
}
export interface TesterImportResult {
  created: number;
  errors: ImportError[];
}
export interface TillerLinkImportResult {
  linked: number;
  errors: ImportError[];
}

export const adminImports = {
  downloadAccountsTemplate: () =>
    apiDownload(
      '/admin/imports/accounts/template',
      'accounts-import-template.xlsx',
    ),
  downloadTestersTemplate: () =>
    apiDownload(
      '/admin/imports/testers/template',
      'testers-import-template.xlsx',
    ),
  downloadTillerLinksTemplate: () =>
    apiDownload(
      '/admin/imports/tiller-links/template',
      'tiller-links-import-template.xlsx',
    ),
  importAccounts: (rows: Record<string, unknown>[]) =>
    apiFetch<AccountImportResult>('/admin/imports/accounts', {
      method: 'POST',
      body: JSON.stringify({ rows }),
    }),
  importTesters: (rows: Record<string, unknown>[]) =>
    apiFetch<TesterImportResult>('/admin/imports/testers', {
      method: 'POST',
      body: JSON.stringify({ rows }),
    }),
  importTillerLinks: (rows: Record<string, unknown>[]) =>
    apiFetch<TillerLinkImportResult>('/admin/imports/tiller-links', {
      method: 'POST',
      body: JSON.stringify({ rows }),
    }),
};

export const alerts = {
  staleTiller: () =>
    apiFetch<{ data: StaleTillerRow[]; threshold_days: number }>('/alerts/stale-tiller'),
  staleUpdates: (hideWithinDays = 3) =>
    apiFetch<{ data: StaleUpdateRow[]; hide_within_days: number }>(
      `/alerts/stale-updates?hide_within_days=${hideWithinDays}`,
    ),
  manualPending: (period: string) =>
    apiFetch<{ data: ManualPendingRow[]; period: string }>(
      `/alerts/manual-pending/${period}`,
    ),
  integratedWithoutTiller: () =>
    apiFetch<{ data: import('@frt/shared-types').IntegratedWithoutTillerRow[] }>(
      '/alerts/integrated-without-tiller',
    ),
};

export const tillerUnmapped = {
  list: () =>
    apiFetch<{ data: import('@frt/shared-types').UnmappedTillerAccount[] }>(
      '/tiller/unmapped',
    ),
};
